/** @jsxImportSource @compiled/react */
import { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { JiraIcon } from "@atlaskit/logo-next";

import { experiments, useExperiment } from "@/utils/experiment";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

import { SmartBanner } from "./smart-banner";

const STORAGE_KEY = "SmartBanner_dismiss-jira";
const DISMISS_EXPIRY = 24 * 60 * 60;

const IOS_APP_STORE_URL = "https://apps.apple.com/app/jira-cloud/id1006972087";
const APPLE_APP_ID = "1006972087";

// Android Intent URL with built-in Play Store fallback
const ANDROID_PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=com.atlassian.android.jira.core";
const ANDROID_INTENT_URL = `intent://home#Intent;scheme=jira;package=com.atlassian.android.jira.core;S.browser_fallback_url=${encodeURIComponent(
    ANDROID_PLAY_STORE_URL
)};end`;

type MobileDeviceInfo = {
    isMobile: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    isSafari: boolean;
};

const useMobileDeviceInfo = (): MobileDeviceInfo => {
    const [deviceInfo, setDeviceInfo] = useState<MobileDeviceInfo>({
        isMobile: false,
        isIOS: false,
        isAndroid: false,
        isSafari: false
    });

    useEffect(() => {
        if (typeof window !== "undefined" && window.navigator) {
            const userAgent = window.navigator.userAgent;
            const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
            const isAndroid = /Android/i.test(userAgent);
            const isSafari =
                /^((?!chrome|android|crios|fxios).)*safari/i.test(userAgent) &&
                isIOS;
            setDeviceInfo({
                isMobile: isIOS || isAndroid,
                isIOS,
                isAndroid,
                isSafari
            });
        }
    }, []);

    return deviceInfo;
};

export interface JiraSmartBannerProps {
    className?: string;
}

/**
 * SmartBanner - iOS/Android smart app banner for Jira
 *
 * This component:
 * - Only renders on iOS and Android devices
 * - Only renders on /software/jira and /software/jira/* paths
 * - Is controlled by the gwp9507JiraBannerIosNativeApp experiment
 * - Persists dismiss state in localStorage for 24 hours
 * - Attempts to open the Jira app via deep link, falls back to app store
 */
export const JiraSmartBanner = ({ className }: JiraSmartBannerProps) => {
    const { isMobile, isAndroid, isSafari } = useMobileDeviceInfo();
    const [isDismissed, setIsDismissed] = useState(true);
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const experimentStatus = useExperiment(
        experiments.gwp9507JiraBannerIosNativeApp
    );

    const isExperimentEnabled =
        experimentStatus.type === "enrolled" && experimentStatus.value === true;

    useEffect(() => {
        if (isExperimentEnabled && isSafari) {
            const meta = document.createElement("meta");
            meta.name = "apple-itunes-app";
            meta.content = `app-id=${APPLE_APP_ID}, ct=wac_smart_banner`;
            document.head.appendChild(meta);

            return () => {
                if (meta.parentNode) {
                    meta.parentNode.removeChild(meta);
                }
            };
        }
    }, [isExperimentEnabled, isSafari]);

    useEffect(() => {
        const dismissValue = getLocalStorage(STORAGE_KEY, true);
        if (dismissValue !== "1") {
            setIsDismissed(false);
        }
    }, []);

    const handleClose = () => {
        setIsDismissed(true);
        setLocalStorage(STORAGE_KEY, "1", DISMISS_EXPIRY);
        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "smartBannerClose",
            eventType: "ui",
            attributes: {
                productKey: "jira",
                eventContainer: "smartBanner"
            }
        }).fire();
    };

    const getDeepLinkUrl = useCallback(() => {
        if (isAndroid) {
            return ANDROID_INTENT_URL;
        }
        return IOS_APP_STORE_URL;
    }, [isAndroid]);

    if (!isMobile || !isExperimentEnabled || isDismissed || isSafari) {
        return null;
    }

    return (
        <SmartBanner
            appIcon={
                <JiraIcon
                    appearance="brand"
                    size="medium"
                    shouldUseNewLogoDesign
                />
            }
            title={intl.formatMessage({
                id: "JiraSmartBanner.title",
                defaultMessage: "Get the Jira app"
            })}
            subtitle={intl.formatMessage({
                id: "JiraSmartBanner.subtitle",
                defaultMessage: "Track tasks, projects, and work"
            })}
            buttonText={intl.formatMessage({
                id: "JiraSmartBanner.buttonText",
                defaultMessage: "OPEN"
            })}
            buttonHref={getDeepLinkUrl()}
            onClose={handleClose}
            className={className}
            dataEventProps={{
                "data-product-key": "jira",
                "data-event-container": "smartBanner",
                "data-label": "OPEN",
                "data-label-english": "OPEN"
            }}
        />
    );
};

export default JiraSmartBanner;
